import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Select } from 'antd';
import { debounce } from 'lodash';
import { PlusIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import axiosInstance from '../../axios/axiosInstance';
import {
    ActionButton,
    DataTable,
    FilterPills,
    PageCard,
    PageShell,
    PageToolbar,
    TechRequestStatusBadge,
} from '../../components/ui';
import {
    fetchTechnicianRequests,
    fetchTeethes,
    firstApiError,
    formatTechPrice,
    PAGE_SIZE,
    TECH_STATUSES,
    toothNumberById,
} from '../../services/technicianRequestService';

function toastLoadError(error, t) {
    const code = error?.response?.status;
    if (code === 401) return;
    if (code === 403) toast.error(t('techRequest.forbidden'));
    else toast.error(firstApiError(error) || t('techRequest.loadError'));
}

function titleForScope(scope, t) {
    if (scope === 'doctor') return t('techRequest.requestsTitleDoctor');
    if (scope === 'technician') return t('techRequest.requestsTitleTech');
    return t('techRequest.requestsTitle');
}

function detailPath(scope, id) {
    if (scope === 'doctor') return `/doctor/technician-requests/${id}`;
    if (scope === 'technician') return `/technician/requests/${id}`;
    return `/admin/technician-requests/${id}`;
}

export default function TechnicianRequests({ scope }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('all');
    const [patientId, setPatientId] = useState(null);
    const [patientOptions, setPatientOptions] = useState([]);
    const [pagination, setPagination] = useState({ current: 1, pageSize: PAGE_SIZE, total: 0 });
    const [teethList, setTeethList] = useState([]);

    const searchPatients = useMemo(
        () =>
            debounce(async (text) => {
                if (!text || text.trim().length < 2) {
                    setPatientOptions([]);
                    return;
                }
                try {
                    const response = await axiosInstance.get('/patients', {
                        params: { name: text.trim(), pageSize: 20 },
                    });
                    const list = response.data.data ?? [];
                    setPatientOptions(
                        list.map((p) => ({
                            value: p.id,
                            label: `${p.name}${p.phone ? ` · ${p.phone}` : ''}${p.code ? ` · ${p.code}` : ''}`,
                        }))
                    );
                } catch {
                    setPatientOptions([]);
                }
            }, 400),
        []
    );

    useEffect(() => () => searchPatients.cancel(), [searchPatients]);

    useEffect(() => {
        fetchTeethes()
            .then(setTeethList)
            .catch(() => setTeethList([]));
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const result = await fetchTechnicianRequests(scope, {
                patientId,
                statuses: statusFilter === 'all' ? undefined : [statusFilter],
                page: pagination.current,
                perPage: pagination.pageSize,
            });
            setItems(result.items);
            setPagination((prev) => ({
                ...prev,
                current: result.pagination.current,
                total: result.pagination.total,
            }));
        } catch (error) {
            setItems([]);
            toastLoadError(error, t);
        } finally {
            setLoading(false);
        }
    }, [scope, patientId, statusFilter, pagination.current, pagination.pageSize, t]);

    useEffect(() => {
        load();
    }, [load]);

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            width: 72,
            render: (id) => <span className="tabular-nums text-sm text-slate-600">{id}</span>,
        },
        {
            title: t('common.patient'),
            key: 'patient',
            render: (_, record) => (
                <div className="min-w-0">
                    <span className="block truncate text-sm font-medium text-[#1D2C7B]">
                        {record.patient?.name ?? t('common.dash')}
                    </span>
                    <span className="mt-0.5 block text-xs text-slate-500">
                        {record.patient?.code || record.patient?.phone || t('common.dash')}
                    </span>
                </div>
            ),
        },
        {
            title: t('techRequest.object'),
            dataIndex: ['object', 'name'],
            render: (name) => <span className="text-sm text-slate-700">{name || t('common.dash')}</span>,
        },
        {
            title: t('techRequest.tooth'),
            dataIndex: 'teeth_id',
            width: 80,
            render: (teethId) => (
                <span className="tabular-nums text-sm text-slate-700">
                    {toothNumberById(teethList, teethId) ?? t('common.dash')}
                </span>
            ),
        },
        {
            title: scope === 'technician' ? t('techRequest.orderedBy') : t('techRequest.technician'),
            key: 'person',
            render: (_, record) => (
                <span className="text-sm text-slate-700">
                    {scope === 'technician'
                        ? record.doctor?.name ?? record.created_by?.name ?? t('common.dash')
                        : record.technician?.name ?? t('common.dash')}
                </span>
            ),
        },
        {
            title: t('techRequest.finalPrice'),
            dataIndex: 'price',
            width: 140,
            render: (value) => (
                <span className="tabular-nums text-sm text-slate-700">
                    {formatTechPrice(value)} {t('common.uzs')}
                </span>
            ),
        },
        {
            title: t('common.status'),
            dataIndex: 'status',
            width: 140,
            render: (status) => <TechRequestStatusBadge status={status} />,
        },
        {
            title: t('common.createdAt'),
            dataIndex: 'created_at',
            width: 120,
            render: (value) => (
                <span className="text-xs tabular-nums text-slate-500">
                    {value ? dayjs(value).format('DD.MM.YYYY') : t('common.dash')}
                </span>
            ),
        },
    ];

    return (
        <PageShell>
            <PageCard>
                <PageToolbar
                    title={titleForScope(scope, t)}
                    count={pagination.total}
                    filters={
                        scope === 'technician' ? null : (
                            <Select
                                allowClear
                                showSearch
                                filterOption={false}
                                size="small"
                                className="md:!w-[220px]"
                                placeholder={t('techRequest.selectPatient')}
                                value={patientId}
                                options={patientOptions}
                                onSearch={searchPatients}
                                onChange={(value) => {
                                    setPatientId(value ?? null);
                                    setPagination((prev) => ({ ...prev, current: 1 }));
                                }}
                            />
                        )
                    }
                    actions={
                        scope === 'doctor' ? (
                            <ActionButton
                                icon={<PlusIcon className="h-4 w-4" />}
                                onClick={() => navigate('/doctor/technician-requests/new')}
                            >
                                {t('techRequest.newOrder')}
                            </ActionButton>
                        ) : null
                    }
                />
                <FilterPills
                    value={statusFilter}
                    onChange={(value) => {
                        setStatusFilter(value);
                        setPagination((prev) => ({ ...prev, current: 1 }));
                    }}
                    options={[
                        { value: 'all', label: t('common.all') },
                        ...TECH_STATUSES.map((status) => ({
                            value: status,
                            label: t(status === 'in_progress' ? 'techRequest.inProgress' : `techRequest.${status}`),
                        })),
                    ]}
                />
                <DataTable
                    columns={columns}
                    dataSource={items}
                    loading={loading}
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        showSizeChanger: false,
                    }}
                    onChange={(pager) => setPagination((prev) => ({ ...prev, current: pager.current }))}
                    onRow={(record) => ({
                        onClick: () => navigate(detailPath(scope, record.id)),
                        className: 'cursor-pointer',
                    })}
                    scrollX={900}
                    mobileCard={(record) => (
                        <Link to={detailPath(scope, record.id)} className="block px-1 py-1 text-inherit no-underline">
                            <div className="flex items-start justify-between gap-2">
                                <span className="text-sm font-semibold text-[#1D2C7B]">
                                    {record.patient?.name ?? record.object?.name ?? `#${record.id}`}
                                </span>
                                <TechRequestStatusBadge status={record.status} />
                            </div>
                            <p className="mb-0 mt-1 text-xs text-slate-500">
                                {record.object?.name} · {t('techRequest.tooth')} {toothNumberById(teethList, record.teeth_id) ?? t('common.dash')} · {formatTechPrice(record.price)} {t('common.uzs')}
                            </p>
                        </Link>
                    )}
                />
            </PageCard>
        </PageShell>
    );
}
